import type { DestroyOptions } from "pixi.js";
import type { IAnimateOptions } from "pixi-viewport";
import { Viewport } from "pixi-viewport";
import type { MapCoordinates } from "./MapCoordinates";
import type { WorldRect } from "./types";

type CustomViewportOptions = ConstructorParameters<typeof Viewport>[0] & {
    coordinates: MapCoordinates;
    sceneRect: WorldRect;
};

export const MIN_WORLD_VISIBLE_SPAN = 0.1;

export class CustomViewport extends Viewport {
    public isNavigating = false;

    private isDragging = false;
    private isPinching = false;
    private navigationStateUpdateTimer: number | null = null;

    constructor(options: CustomViewportOptions) {
        super(options);

        const bounceMargin = options.coordinates.worldLengthToScene(20);

        this.bounce({
            sides: "all",
            friction: 0.5,
            time: 10,
            ease: "easeInOutSine",
            underflow: "center",
            bounceBox: {
                x: options.sceneRect.x - bounceMargin,
                y: options.sceneRect.y - bounceMargin,
                width: options.sceneRect.width + 2 * bounceMargin,
                height: options.sceneRect.height + 2 * bounceMargin,
            } as unknown as import("pixi.js").Rectangle,
        });

        this.drag().pinch().wheel({ smooth: 5 });

        this.clampZoom({
            minWidth: options.coordinates.worldLengthToScene(
                MIN_WORLD_VISIBLE_SPAN,
            ),
            minHeight: options.coordinates.worldLengthToScene(
                MIN_WORLD_VISIBLE_SPAN,
            ),
            maxWidth:
                8 * Math.max(options.sceneRect.width, options.sceneRect.height),
            maxHeight:
                8 * Math.max(options.sceneRect.width, options.sceneRect.height),
        });

        this.on("drag-start", this.handleDragStart);
        this.on("drag-end", this.handleDragEnd);
        this.on("pinch-start", this.handlePinchStart);
        this.on("pinch-end", this.handlePinchEnd);
        this.on("pointerup", this.handlePointerRelease);
        this.on("pointerupoutside", this.handlePointerRelease);
        this.on("pointercancel", this.handlePointerRelease);
        this.on("pointerleave", this.handlePointerRelease);
    }

    public get isUsable(): boolean {
        return !this.destroyed && Boolean(this.scale);
    }

    public override animate(options: IAnimateOptions): CustomViewport {
        this.emit("camera-move-start", this);
        super.animate(options);
        return this;
    }

    public override destroy(options?: DestroyOptions): void {
        this.emit("viewport-destroy", this);

        this.off("drag-start", this.handleDragStart);
        this.off("drag-end", this.handleDragEnd);
        this.off("pinch-start", this.handlePinchStart);
        this.off("pinch-end", this.handlePinchEnd);
        this.off("pointerup", this.handlePointerRelease);
        this.off("pointerupoutside", this.handlePointerRelease);
        this.off("pointercancel", this.handlePointerRelease);
        this.off("pointerleave", this.handlePointerRelease);

        if (this.navigationStateUpdateTimer !== null) {
            window.clearTimeout(this.navigationStateUpdateTimer);
            this.navigationStateUpdateTimer = null;
        }

        super.destroy(options);
    }

    private startNavigation(): void {
        if (this.isNavigating) return;

        this.isNavigating = true;
        this.emit("navigation-start", this);
    }

    private scheduleNavigationStateUpdate(): void {
        if (this.navigationStateUpdateTimer !== null) {
            window.clearTimeout(this.navigationStateUpdateTimer);
        }

        this.navigationStateUpdateTimer = window.setTimeout(() => {
            this.navigationStateUpdateTimer = null;

            if (this.input.count() === 0) {
                this.isDragging = false;
                this.isPinching = false;
            }

            const wasNavigating = this.isNavigating;
            this.isNavigating = this.isDragging || this.isPinching;

            if (wasNavigating && !this.isNavigating) {
                this.emit("navigation-end", this);
            }
        }, 0);
    }

    private readonly handleDragStart = (): void => {
        this.isDragging = true;
        this.startNavigation();
    };

    private readonly handleDragEnd = (): void => {
        this.isDragging = false;
        this.scheduleNavigationStateUpdate();
    };

    private readonly handlePinchStart = (): void => {
        this.isPinching = true;
        this.startNavigation();
    };

    private readonly handlePinchEnd = (): void => {
        this.isPinching = false;
        this.scheduleNavigationStateUpdate();
    };

    private readonly handlePointerRelease = (): void => {
        this.scheduleNavigationStateUpdate();
    };
}
